import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, X, Calendar, ArrowRight, Clock, Star,
  Home, TrendingUp, Plane, MapPin, Coffee, BookOpen,
} from 'lucide-react';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import PopularArticles from '../components/PopularArticles';
import { useLanguage } from '../context/LanguageContext';
import CATEGORIES from '../config/blogCategories';

const API = process.env.REACT_APP_BACKEND_URL;

/* Map icon names from the category config to actual lucide components. Keeps
   the config JSON-serialisable (no JSX inside it) so it can move to a CMS later. */
const ICONS = { Home, TrendingUp, Plane, MapPin, Coffee, BookOpen };

/* Region filter chips. Distinct from `category` because users frequently want
   to slice by location across multiple topics (e.g. "all Costa Blanca posts,
   regardless of whether they are about lifestyle or buying"). */
const REGION_TAGS = [
  { id: 'costa-blanca', nl: 'Costa Blanca', en: 'Costa Blanca', pl: 'Costa Blanca', match: 'costa blanca' },
  { id: 'costa-calida', nl: 'Costa C\u00e1lida', en: 'Costa C\u00e1lida', pl: 'Costa C\u00e1lida', match: 'costa calida' },
  { id: 'costa-almeria', nl: 'Costa Almer\u00eda', en: 'Costa Almer\u00eda', pl: 'Costa Almer\u00eda', match: 'costa almeria' },
];

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');
  const { t } = useLanguage();

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/blog?limit=200`).then(r => r.json()),
      fetch(`${API}/api/blog/categories`).then(r => r.json()),
    ])
      .then(([postsData, catData]) => {
        setPosts(postsData);
        setCategoryCounts(catData);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const formatDate = (iso) => new Date(iso).toLocaleDateString(
    'pl-PL',
    { year: 'numeric', month: 'long', day: 'numeric' },
  );
  const titleOf = (p) => p.title;
  const excerptOf = (p) => p.excerpt;

  /* Client-side text + region filtering. The API supports server-side too but
     for <500 posts this is instant and lets the chips feel snappy. */
  const visiblePosts = useMemo(() => {
    let list = posts;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.title_en || '').toLowerCase().includes(q) ||
        (p.excerpt || '').toLowerCase().includes(q) ||
        (p.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    if (regionFilter !== 'all') {
      const region = REGION_TAGS.find(r => r.id === regionFilter);
      if (region) {
        list = list.filter(p =>
          (p.tags || []).some(t => t.toLowerCase() === region.match)
        );
      }
    }
    return list;
  }, [posts, search, regionFilter]);

  const featuredPosts = visiblePosts.filter(p => p.featured).slice(0, 3);
  const recentPosts = visiblePosts.filter(p => !featuredPosts.includes(p));

  const activeFilterCount = (search.trim() ? 1 : 0) + (regionFilter !== 'all' ? 1 : 0);
  const clearAll = () => { setSearch(''); setRegionFilter('all'); };

  return (
    <div style={styles.page}>
      <SEO titleKey="seo.blogTitle" descriptionKey="seo.blogDescription" />
      <PageHero
        image="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1920&q=80"
        title="Baza wiedzy"
        subtitle="Wszystko, co musisz wiedzieć o życiu, kupowaniu i inwestowaniu w Hiszpanii"
        testIdTitle="blog-title"
      />

      <div style={styles.container}>
        {/* Search + region filters */}
        <div style={styles.searchSection} data-testid="blog-search-section">
          <div style={styles.searchBox}>
            <Search size={18} color="#8C8B88" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj artykułów, tematów, tagów..."
              style={styles.searchInput}
              data-testid="blog-search-input"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} style={styles.clearBtn} aria-label="Clear">
                <X size={16} color="#8C8B88" />
              </button>
            )}
          </div>

          <div style={styles.chipRow} data-testid="blog-region-chips">
            <button
              type="button"
              onClick={() => setRegionFilter('all')}
              style={regionFilter === 'all' ? styles.chipActive : styles.chip}
              data-testid="region-chip-all"
            >
              Wszystkie regiony
            </button>
            {REGION_TAGS.map(r => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRegionFilter(r.id)}
                style={regionFilter === r.id ? styles.chipActive : styles.chip}
                data-testid={`region-chip-${r.id}`}
              >
                {r.pl}
              </button>
            ))}
            {activeFilterCount > 0 && (
              <button type="button" onClick={clearAll} style={styles.clearAllBtn} data-testid="blog-clear-filters">
                <X size={14} /> Wyczyść filtry
              </button>
            )}
          </div>
        </div>

        {/* Category grid — only show when no search active so it's the calm
            default state, not noise mid-search */}
        {!search && regionFilter === 'all' && (
          <section style={styles.section} data-testid="blog-categories-grid">
            <h2 style={styles.sectionTitle}>Tematy</h2>
            <div style={styles.catGrid}>
              {CATEGORIES.map(cat => {
                const Ico = ICONS[cat.icon] || BookOpen;
                const count = categoryCounts[cat.id] || 0;
                return (
                  <Link
                    key={cat.id}
                    to={`/blog/categorie/${cat.id}`}
                    style={count === 0 ? styles.catCardEmpty : styles.catCard}
                    data-testid={`category-card-${cat.id}`}
                  >
                    <div style={styles.catIconWrap}>
                      <Ico size={20} color="#C5A059" strokeWidth={1.4} />
                    </div>
                    <h3 style={styles.catTitle}>{cat.labelPl}</h3>
                    <p style={styles.catCount}>
                      {count} {count === 1 ? 'artykuł' : 'artykułów'}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Featured strip */}
        {featuredPosts.length > 0 && (
          <section style={styles.section} data-testid="blog-featured-section">
            <h2 style={styles.sectionTitle}>
              <Star size={20} color="#C5A059" style={{ verticalAlign: 'middle', marginRight: '0.4rem' }} />
              Wyróżnione artykuły
            </h2>
            <div style={styles.grid}>
              {featuredPosts.map(p => <PostCard key={p.id} post={p} titleOf={titleOf} excerptOf={excerptOf} formatDate={formatDate} t={t} featured />)}
            </div>
          </section>
        )}

        {/* Popular articles — driven by view counts. Only shows in the calm
            default state (no filters/search) so it doesn't compete with the
            search-results header. */}
        {!search && regionFilter === 'all' && (
          <PopularArticles variant="index" limit={3} />
        )}

        {/* All / filtered posts */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            {search || regionFilter !== 'all'
              ? 'Wyniki wyszukiwania'
              : 'Wszystkie artykuły'}
            <span style={styles.resultCount}> ({visiblePosts.length})</span>
          </h2>

          {loading ? (
            <p style={styles.loadingText}>{t('common.loading')}</p>
          ) : recentPosts.length === 0 && featuredPosts.length === 0 ? (
            <div style={styles.emptyState} data-testid="blog-empty">
              <p style={styles.emptyText}>
                Nie znaleziono artykułów spełniających te kryteria.
              </p>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearAll} style={styles.emptyCta}>
                  Wyczyść filtry
                </button>
              )}
            </div>
          ) : (
            <div style={styles.grid}>
              {recentPosts.map(p => <PostCard key={p.id} post={p} titleOf={titleOf} excerptOf={excerptOf} formatDate={formatDate} t={t} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

/* Shared post card — used for both featured and regular grid. The `featured`
   prop adds a subtle gold ribbon and slightly larger typography. */
const PostCard = ({ post, titleOf, excerptOf, formatDate, t, featured }) => (
  <article style={featured ? styles.postCardFeatured : styles.postCard} data-testid="blog-post-card">
    <Link to={`/blog/${post.slug}`} style={styles.cardLink}>
      <div style={styles.imageContainer}>
        <img src={post.cover_image} alt={titleOf(post)} style={styles.image} loading="lazy" data-testid="blog-post-image" />
        {featured && (
          <span style={styles.featuredRibbon}>
            <Star size={11} /> Wyróżnione
          </span>
        )}
      </div>
      <div style={styles.content}>
        <div style={styles.meta}>
          <Calendar size={14} color="#8C8B88" />
          <span style={styles.date}>{formatDate(post.published_at)}</span>
          {post.reading_time > 0 && (
            <>
              <span style={styles.metaDot}>·</span>
              <Clock size={14} color="#8C8B88" />
              <span style={styles.date}>{post.reading_time} min</span>
            </>
          )}
        </div>
        <h3 style={featured ? styles.postTitleFeatured : styles.postTitle} data-testid="blog-post-title">
          {titleOf(post)}
        </h3>
        <p style={styles.excerpt} data-testid="blog-post-excerpt">{excerptOf(post)}</p>
        <span style={styles.readMore} data-testid="read-more-link">
          {t('blog.readMore')}<ArrowRight size={16} style={{ marginLeft: '0.4rem' }} />
        </span>
      </div>
    </Link>
  </article>
);

const styles = {
  page: { minHeight: '100vh', paddingBottom: '4rem', backgroundColor: '#FAF9F6' },
  container: { maxWidth: '1280px', margin: '0 auto', padding: '0 1.5rem' },

  searchSection: {
    backgroundColor: 'white',
    padding: '1.25rem 1.5rem',
    border: '1px solid #EAE5DD',
    margin: '-2.5rem 0 2rem',
    position: 'relative',
    zIndex: 5,
    boxShadow: '0 4px 18px rgba(0,0,0,0.04)',
  },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: '0.6rem',
    paddingBottom: '0.85rem',
    borderBottom: '1px solid #EAE5DD',
    marginBottom: '0.85rem',
  },
  searchInput: {
    flex: 1, border: 'none', outline: 'none',
    fontFamily: "'Manrope', sans-serif",
    fontSize: '0.95rem', color: '#1A1918',
    backgroundColor: 'transparent',
    padding: '0.4rem 0',
  },
  clearBtn: {
    border: 'none', background: 'transparent', cursor: 'pointer', padding: '0.25rem',
  },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' },
  chip: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    color: '#585755',
    padding: '0.4rem 0.9rem',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '999px',
    fontFamily: "'Manrope', sans-serif",
  },
  chipActive: {
    backgroundColor: '#1A1918',
    border: '1px solid #1A1918',
    color: '#C5A059',
    padding: '0.4rem 0.9rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    borderRadius: '999px',
    fontFamily: "'Manrope', sans-serif",
  },
  clearAllBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
    backgroundColor: 'transparent', border: 'none', color: '#8C8B88',
    fontSize: '0.78rem', textDecoration: 'underline', cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif", marginLeft: '0.5rem',
  },

  section: { marginBottom: '3.5rem' },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.6rem',
    color: '#1A1918',
    marginBottom: '1.5rem',
    fontWeight: 500,
  },
  resultCount: { color: '#8C8B88', fontSize: '1rem', fontWeight: 400, fontFamily: "'Manrope', sans-serif" },

  catGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: '0.85rem',
  },
  catCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    padding: '1rem 1rem 0.9rem',
    textDecoration: 'none',
    color: 'inherit',
    transition: 'all 0.2s ease',
    minHeight: '120px',
  },
  catCardEmpty: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
    backgroundColor: '#FAF6EE',
    border: '1px dashed #EAE5DD',
    padding: '1rem 1rem 0.9rem',
    textDecoration: 'none',
    color: 'inherit',
    opacity: 0.55,
    minHeight: '120px',
  },
  catIconWrap: {
    width: '36px', height: '36px',
    backgroundColor: '#FAF6EE',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '0.6rem',
  },
  catTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '0.98rem', color: '#1A1918',
    marginBottom: '0.2rem', fontWeight: 500,
    lineHeight: 1.25,
  },
  catCount: { color: '#8C8B88', fontSize: '0.74rem', margin: 0 },

  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '1.75rem',
  },
  postCard: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    overflow: 'hidden',
    transition: 'all 0.25s ease',
  },
  postCardFeatured: {
    backgroundColor: 'white',
    border: '1px solid #C5A059',
    overflow: 'hidden',
    transition: 'all 0.25s ease',
    boxShadow: '0 6px 20px rgba(197, 160, 89, 0.12)',
  },
  cardLink: { textDecoration: 'none', color: 'inherit', display: 'block' },
  imageContainer: { width: '100%', height: '220px', overflow: 'hidden', position: 'relative' },
  image: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  featuredRibbon: {
    position: 'absolute', top: '0.85rem', left: '0.85rem',
    display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
    backgroundColor: '#C5A059', color: 'white',
    padding: '0.3rem 0.6rem',
    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  content: { padding: '1.5rem' },
  meta: { display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  metaDot: { color: '#C5A059', margin: '0 0.1rem' },
  date: { color: '#8C8B88', fontSize: '0.8rem' },
  postTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.25rem', color: '#1A1918',
    marginBottom: '0.85rem', lineHeight: 1.3, fontWeight: 500,
  },
  postTitleFeatured: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.4rem', color: '#1A1918',
    marginBottom: '0.85rem', lineHeight: 1.25, fontWeight: 500,
  },
  excerpt: { color: '#585755', fontSize: '0.92rem', lineHeight: 1.65, marginBottom: '1.25rem' },
  readMore: {
    display: 'inline-flex', alignItems: 'center',
    color: '#C5A059', fontSize: '0.88rem', fontWeight: 600,
    letterSpacing: '0.03em',
  },

  emptyState: { textAlign: 'center', padding: '3rem 1rem', backgroundColor: 'white', border: '1px solid #EAE5DD' },
  emptyText: { color: '#585755', fontSize: '1rem', marginBottom: '1rem' },
  emptyCta: {
    backgroundColor: '#1A1918', color: '#C5A059', border: 'none',
    padding: '0.7rem 1.5rem', fontSize: '0.82rem', fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer',
    fontFamily: "'Manrope', sans-serif",
  },
  loadingText: { textAlign: 'center', color: '#8C8B88', padding: '3rem 0' },
};

export default Blog;
